import type { HTMLAttributes, ReactNode } from 'react';
import { cx } from './cx';

export function Card({ className, children, ...rest }: HTMLAttributes<HTMLDivElement> & { children: ReactNode }) {
  return <div className={cx('rounded-2xl border border-line bg-white p-5', className)} {...rest}>{children}</div>;
}

/** "Ryan Robertson" → "RR"; falls back to the first letters of an email. */
export function initialsOf(name: string | null | undefined, mode: 'person' | 'firm' = 'person'): string {
  const parts = (name ?? '').replace(/@.*$/, '').split(/[\s._-]+/).filter((w) => /^[A-Za-z]/.test(w));
  if (!parts.length) return '?';
  const second = parts.length > 1 ? (mode === 'firm' ? parts[1]! : parts[parts.length - 1]!) : '';
  return (parts[0]![0]! + (second[0] ?? '')).toUpperCase();
}

/** Circle with initials, e.g. "MN". `tone="soft"` is the pale chip used on navy and in lists. */
export function Avatar({ initials, size = 44, tone = 'brand', square }: { initials: string; size?: number; tone?: 'brand' | 'soft'; square?: boolean }) {
  return (
    <span
      aria-hidden="true"
      style={{ width: size, height: size, fontSize: size <= 24 ? 11 : 13 }}
      className={cx(
        'flex flex-none items-center justify-center font-bold',
        square ? 'rounded-[10px]' : 'rounded-full',
        tone === 'soft' ? 'bg-brand-soft text-brand' : 'bg-brand text-white',
      )}
    >
      {initials}
    </span>
  );
}

/** Thin progress track. Pass a label to expose it as an image to screen readers. */
export function ProgressBar({ pct, label, height = 6, className }: { pct: number; label?: string; height?: number; className?: string }) {
  const w = Math.max(0, Math.min(100, Math.round(pct)));
  return (
    <span
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      style={{ height }}
      className={cx('block overflow-hidden rounded-full bg-line', className)}
    >
      <span style={{ width: `${w}%`, height }} className="block rounded-full bg-brand transition-[width]" />
    </span>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div role="status" className="flex items-center gap-3 p-8 text-muted">
      <span aria-hidden="true" className="h-5 w-5 animate-spin rounded-full border-2 border-line border-t-brand" />
      <span>{label}…</span>
    </div>
  );
}

/**
 * White on transparent. `staff` is the Vertekal wordmark (785 × 110, from the VertekCore brand assets) on staff screens;
 * firm pages keep the Upstream logo from Matt's prototype until their wording moves to Vertekal.
 */
export function Wordmark({ className, staff }: { className?: string; staff?: boolean }) {
  if (staff) return <img src="/vertekal-logo.png" alt="Vertekal" width={118} height={17} className={cx('block h-[17px] w-auto', className)} />;
  return <img src="/upstream-logo.png" alt="Upstream" width={70} height={26} className={cx('block h-[26px] w-auto', className)} />;
}
